import { useEffect, FC, useReducer, useState, useRef } from 'react';
import type { AnyAction } from '@reduxjs/toolkit';
import {
  Button,
  ButtonStyles,
  ButtonSize,
  ButtonTypes,
  Drawer,
  Input,
  PhoneNumber,
  InputTypes,
  useTranslation,
  MLPVersion,
  Theme,
  FAIcon,
  DrawerDirections,
  IconStyle,
  Shade,
  ButtonIconPosition,
  IconAnimationClassName,
  AlertMessage,
  useBreakpoint,
  Breakpoints,
  TextLink,
  TextLinkStyle,
  TextLinkIconPosition,
  DropDownValidations,
  NextgenImage,
  SkeletonFrame,
  LoaderType,
} from '@nextgen-web-framework/all';
import {
  Checkout,
  resetPickupStoreOptions,
  CheckoutProfileService,
  ILoadingObject,
  getFulfillmentCmsDataSliceReq,
  getStoreLocationsSliceReq,
  ICheckoutPickupStoresOptions,
  getValueCheck,
  DataTypesEnums,
  checkoutFulfillmentOptions,
  isEasyDeliveryOrBopisOption,
  ICheckoutPickupStoresForParamSearch,
} from '@commerce-checkout-webcomponents/sdk';

import { GoogleMaps, ErrorCard, Errors } from '@commerce-webcomponents/ui';
import type { AppState, AppDispatch } from '@nextgen-web-framework/core';
import { Logger } from '@nextgen-web-framework/core';
import { useSelector, useDispatch } from 'react-redux';
import {
  IFulfillmentTypesAndStoreMap,
  IkeyStringWithKeyStringObject,
} from '@commerce-checkout-webcomponents/sdk/types';

import {
  shippingDetailsPickupDrawerInitialState,
  shippingDetailsPickupDrawerReducer,
  transformCheckoutToFulfillmentRequest,
} from '../../common/helpers/CheckoutShippingHelpers';
import { handleMaxLengthCheck, filterArrayObjectByKeys, parseContactNumber } from '../../../utils/CheckoutUtils';
import CheckoutStoreOptionRadioCard from '../checkout-store-option-radio-card/CheckoutStoreOptionRadioCard';
import {
  LoaderTypeEnums,
  checkoutShippingDetailsActions,
  storeFieldForSearch,
  countryDropdownLabelField,
  dayLabels,
  errorImageUrl,
  LocationPermission,
  geoLocation,
  noStoreErrorImageUrl,
  checkoutPostalCodeLength6,
  checkoutPostalCodeLength5,
} from '../../common/config/checkoutConstants';
import CheckoutPageLoader from '../../checkout-page-loader/CheckoutPageLoader';
import { ICheckoutPickupDrawerProps } from './../types/propTypes';
import styles from './CheckoutPickupDrawer.module.scss';
import { usePhoneNumberData } from './../../common/custom-hooks/usePhoneNumberData';

export const CheckoutPickupDrawer: FC<ICheckoutPickupDrawerProps> = ({
  onCloseDrawer,
  onConfrimPickupStore,
  selectedStoreForPickup,
  bopisDetails,
  entries,
  currencyIsoCode,
  contactFieldEnabled,
  confirmButtonLoading,
  locationPermission,
  setLocationPermission,
  showAvailabilityTag,
}: ICheckoutPickupDrawerProps) => {
  const t = useTranslation();
  const viewPortType = useBreakpoint();
  const logger = Logger.getInstance();
  const isMobileView = viewPortType == Breakpoints.XS || viewPortType == Breakpoints.SM;
  const isTabletView = viewPortType == Breakpoints.MD;
  const isMdTabletView = viewPortType == Breakpoints.LG;
  const isDesktopView = viewPortType == Breakpoints.XL;
  const countryMasterData = usePhoneNumberData();
  const checkoutProfile = CheckoutProfileService.getCheckoutProfile();
  const checkout: Checkout = useSelector((state: AppState) => state.commerceCheckout.checkout);
  const configs = ConfigService.getConfigs();
  const pickupLatamFlow = configs.NEXT_PUBLIC_ENABLE_PICKUP_LATAM_FLOW as boolean;
  const checkoutPostalCodeLength = pickupLatamFlow ? checkoutPostalCodeLength6 : checkoutPostalCodeLength5;
  const assetsBasePath = configs.NEXT_ASSETS_BASE_PATH as string;
  const [initialPickupStoresList, setInitialPickupStoresList] = useState<ICheckoutPickupStoresForParamSearch>();
  const [showErrorCard, setShowErrorCard] = useState<boolean>(false);
  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState<boolean>(true);
  const dispatch = useDispatch<AppDispatch>();
  const fulfillmentTypesAndStoreMap: IFulfillmentTypesAndStoreMap = useSelector(
    (state: AppState) => state.commerceCheckout.fulfillmentTypesAndStoreMap,
  );

  const fulfillmentCmsData: IkeyStringWithKeyStringObject = useSelector(
    (state: AppState) => state.commerceCheckout.fulfillmentCmsContent,
  );
  const loadingState: ILoadingObject = useSelector((state: AppState) => state?.commerceCheckout?.loadingState);

  const getDefaultStoreOption = (): string => Object.keys(fulfillmentTypesAndStoreMap?.storeMap)[0] || '';
  const getStoreOptionName = (): string => {
    if (checkout?.fulfillment?.id && checkout?.fulfillment?.id != checkoutFulfillmentOptions.HOMEDELIVERY) {
      return checkout.fulfillment.id;
    }
    if (
      checkoutProfile?.fulfillmentType?.id &&
      checkoutProfile?.fulfillmentType?.id != checkoutFulfillmentOptions.HOMEDELIVERY
    ) {
      return checkoutProfile.fulfillmentType.id;
    }
    return getDefaultStoreOption();
  };
  const getPhoneNumber = (contact: string) => parseContactNumber(contact, true);
  const pickupContactNumberWithCode = getPhoneNumber(
    bopisDetails?.countryCode && bopisDetails?.mobileNo
      ? bopisDetails.countryCode + bopisDetails.mobileNo
      : getValueCheck(checkout?.user?.mobileNo, DataTypesEnums.TYPE_STRING),
  );
  const needPostalResetRef = useRef(true);
  const [state, dispatchStateAction] = useReducer(shippingDetailsPickupDrawerReducer, {
    ...shippingDetailsPickupDrawerInitialState,
    recipientContact: pickupContactNumberWithCode.phone,
    recipientCountryCode:
      pickupContactNumberWithCode.countryCode || countryMasterData.defaultCountryCode || bopisDetails?.countryCode,
    storePin:
      locationPermission != LocationPermission.GRANTED
        ? fulfillmentTypesAndStoreMap?.pincode || selectedStoreForPickup?.address?.postCode || ''
        : '',
    selectedStoreId: selectedStoreForPickup?.id,
    storeOptionName: getStoreOptionName(),
  });

  const selectedStoreOptionRef = useRef(state.storeOptionName);

  const getStoreList = (
    pincode: string,
    storeChipName: string,
    isStorePinSearchEvent?: boolean,
    lat?: number,
    long?: number,
    pagination?: boolean,
  ) => {
    needPostalResetRef.current = Boolean(isStorePinSearchEvent);
    const storeList = fulfillmentTypesAndStoreMap.storeMap?.[state.storeOptionName]?.storeList;
    const lastStoreId = storeList?.[storeList.length - 1]?.id;
    if (state.enableLocationCtaVisibility) {
      dispatchStateAction({ type: checkoutShippingDetailsActions.SET_ENABLE_LOCATION_CTA_VISIBILITY, payload: false });
    }
    if (!isStorePinSearchEvent && state.storePin) {
      dispatchStateAction({ type: checkoutShippingDetailsActions.SET_STORE_PIN, payload: '' });
    }
    if (isStorePinSearchEvent && pincode && !isEasyDeliveryOrBopisOption(state.storeOptionName)) {
      dispatchStateAction({ type: checkoutShippingDetailsActions.SET_STORE_PIN, payload: pincode });
    }
    dispatch(
      getStoreLocationsSliceReq(
        transformCheckoutToFulfillmentRequest(
          entries || [],
          storeChipName,
          pincode,
          currencyIsoCode,
          isStorePinSearchEvent,
          lat,
          long,
          fulfillmentTypesAndStoreMap?.storeMap?.[storeChipName]?.lastEvaluatedKey,
          pagination,
        ),
      ) as unknown as AnyAction,
    ).then((response: AnyAction) => {
      pagination && lastStoreId && scrollToSelectedStore(lastStoreId);
      dispatchStateAction({ type: checkoutShippingDetailsActions.SET_PICKUP_DRAWER_LOADING, payload: false });
      if (response?.error) {
        setShowErrorCard(true);
      }
    });
  };

  const fetchStoreListUsingDefaults = (postCode?: string, storeChip?: string) => {
    const storeLat = selectedStoreForPickup?.address?.lat ? Number(selectedStoreForPickup?.address?.lat) : undefined;
    const storeLong = selectedStoreForPickup?.address?._long
      ? Number(selectedStoreForPickup?.address?._long)
      : undefined;
    const isStorePinSearchEvent = Boolean(!storeLat || !storeLong);
    needPostalResetRef.current = isStorePinSearchEvent;
    getStoreList(
      postCode ||
        selectedStoreForPickup?.address?.postCode ||
        fulfillmentTypesAndStoreMap?.pincode ||
        checkoutProfile?.fulfillmentType?.serviceLevel?.deliveryAddress?.postalCode ||
        '',
      storeChip || state.storeOptionName,
      isStorePinSearchEvent,
      storeLat,
      storeLong,
    );
  };

  const handleLocationPermission = () => {
    navigator.permissions.query({ name: geoLocation }).then((permissionStatus) => {
      setLocationPermission(permissionStatus.state);
      permissionStatus.onchange = () => {
        showCurrentLocation(selectedStoreOptionRef.current, false);
      };
    });
  };

  useEffect(() => {
    dispatchStateAction({ type: checkoutShippingDetailsActions.SET_PICKUP_DRAWER_LOADING, payload: true });
    if (locationPermission && locationPermission == LocationPermission.DENIED) {
      fetchStoreListUsingDefaults();
    }
  }, [locationPermission]);

  useEffect(() => {
    if (!fulfillmentCmsData || !Object.keys(fulfillmentCmsData)?.length) {
      dispatch(getFulfillmentCmsDataSliceReq() as unknown as AnyAction);
    }
    showCurrentLocation(state.storeOptionName, false);
    if (!locationPermission) {
      handleLocationPermission();
    }
  }, []);

  useEffect(() => {
    if (fulfillmentTypesAndStoreMap) {
      dispatchStateAction({ type: checkoutShippingDetailsActions.SET_PICKUP_DRAWER_LOADING, payload: false });
      setStoreData(state.storeOptionName);
    }
  }, [fulfillmentTypesAndStoreMap]);

  const getPostalFieldConfig = () => ({
    ...(isEasyDeliveryOrBopisOption(state.storeOptionName)
      ? { type: InputTypes.TEXT, label: t('checkout.CheckoutPickupDrawer.drawerFormLabelPinAddress') }
      : {
          type: InputTypes.NUMBER,
          maxLength: checkoutPostalCodeLength,
          onInput: handleMaxLengthCheck,
          onKeyDown: (key: React.KeyboardEvent<HTMLInputElement>) => handleStoreSearch(state.storePin, key.code),
          label: t('checkout.CheckoutPickupDrawer.drawerFormLabelPinAddressThirdParty'),
        }),
  });

  const setStoreData = (selectedStoreNameId: string) => {
    const storeMapData = structuredClone(fulfillmentTypesAndStoreMap.storeMap);
    Object.keys(storeMapData).forEach((storeOptionName) => {
      storeOptionName == selectedStoreNameId
        ? (storeMapData[storeOptionName].isSelected = true)
        : (storeMapData[storeOptionName].isSelected = false);
    });
    dispatchStateAction({ type: checkoutShippingDetailsActions.SET_STORE_MAP, payload: storeMapData });
    dispatchStateAction({ type: checkoutShippingDetailsActions.SET_STORE_OPTION_NAME, payload: selectedStoreNameId });
    dispatchStateAction({
      type: checkoutShippingDetailsActions.SET_STORE_TYPE_OPTIONS,
      payload: Object.keys(storeMapData),
    });
    updateStoresListWithSelectedStore(storeMapData[selectedStoreNameId]?.storeList);
    setInitialPickupStoresList({
      ...initialPickupStoresList,
      [selectedStoreNameId]: storeMapData[selectedStoreNameId]?.storeList,
    });
  };

  const updateStoresListWithSelectedStore = (storeList: Array<ICheckoutPickupStoresOptions>) => {
    if (state.selectedStoreId) {
      const selectedStore = storeList?.filter((store) => store.id == state.selectedStoreId)?.[0];
      const updatesStoresData = storeList?.filter((store) => store.id != state.selectedStoreId);
      if (selectedStore) {
        updatesStoresData.unshift(selectedStore);
        dispatchStateAction({
          type: checkoutShippingDetailsActions.SET_SELECTED_SERVICE_LOCATION,
          payload: selectedStore.serviceLocation,
        });
      }
      dispatchStateAction({ type: checkoutShippingDetailsActions.SET_STORE_OPTIONS_DATA, payload: updatesStoresData });
    } else {
      dispatchStateAction({ type: checkoutShippingDetailsActions.SET_STORE_OPTIONS_DATA, payload: storeList });
    }
  };

  const storeListTitle = () => {
    const yourLocationText = t('checkout.CheckoutPickupDrawer.drawerYourLocationText');
    const selectedStoreId = selectedStoreForPickup?.id || checkoutProfile?.fulfillmentType?.serviceLocation?.id;
    const storesCount: string = !loadingState?.getStoreLocations?.isLoading
      ? `(${
          state.storeOptionsData?.filter((store: ICheckoutPickupStoresOptions) => store.id != selectedStoreId).length
        })`
      : '';
    if (
      fulfillmentTypesAndStoreMap?.pincode &&
      fulfillmentTypesAndStoreMap?.pincode.length == checkoutPostalCodeLength &&
      !isEasyDeliveryOrBopisOption(state.storeOptionName)
    ) {
      return t('checkout.CheckoutPickupDrawer.drawerStoreListHeader', {
        address: fulfillmentTypesAndStoreMap?.pincode,
        count: storesCount,
      });
    } else if (
      (!fulfillmentTypesAndStoreMap?.pincode ||
        (fulfillmentTypesAndStoreMap?.pincode &&
          fulfillmentTypesAndStoreMap?.pincode.length != checkoutPostalCodeLength)) &&
      locationPermission == LocationPermission.GRANTED
    ) {
      return t('checkout.CheckoutPickupDrawer.drawerStoreListHeader', {
        address: yourLocationText,
        count: storesCount,
      });
    }
    return t('checkout.CheckoutPickupDrawer.drawerStoreListTitle', { storesCount });
  };

  const errorCardActionHandler = () => {
    if (state.storePin && state.storePin.length >= checkoutPostalCodeLength) {
      getStoreList(state.storePin, selectedStoreOptionRef.current, true);
    } else if (locationPermission == LocationPermission.GRANTED) {
      showCurrentLocation(selectedStoreOptionRef.current, false);
    } else {
      fetchStoreListUsingDefaults('', selectedStoreOptionRef.current);
    }
  };

  const getErrorCard = () => (
    <ErrorCard
      className={`${styles['store-api-error-card']} ${showErrorCard && styles['store-api-error-card-visible']}`}
      title={t('checkout.CheckoutPickupDrawer.errorTitle')}
      description={t('checkout.CheckoutPickupDrawer.errorDescription')}
      buttonLabel={t('checkout.CheckoutPickupDrawer.errorCTA')}
      imageUrl={`${assetsBasePath}${errorImageUrl}`}
      onActionClick={() => {
        setShowErrorCard(false);
        errorCardActionHandler();
      }}
      type={Errors.General}
    />
  );

  const noStoreAndMobileCheck = () => state.storeOptionsData?.length > 0 || !isMobileView;

  const getListOfStores = () => {
    const selectedStoreId = selectedStoreForPickup?.id || checkoutProfile?.fulfillmentType?.serviceLocation?.id;
    return (
      <div className={styles['store-list-container']}>
        {!loadingState?.getStoreLocations?.isLoading && noStoreAndMobileCheck() && (
          <h4 className={styles['store-availability']}>{storeListTitle()}</h4>
        )}
        {fulfillmentTypesAndStoreMap?.pickUp?.[state.storeOptionName]?.isRestricted && (
          <AlertMessage
            icon="exclamation-triangle"
            theme={Theme.WARNING}
            iconStyle={IconStyle.REGULAR}
            className={styles['checkout-selected-address-alert-msg']}
          >
            {t('checkout.CheckoutShippingDetailsContainer.pickupNotAvailable', {
              selectedStoreOptionName: state.storeMap?.[state.storeOptionName]?.storeName,
            })}
          </AlertMessage>
        )}
        {loadingState?.getStoreLocations?.isLoading ? (
          <CheckoutPageLoader loaderType={LoaderTypeEnums.STORE_LIST} />
        ) : (
          <>
            {noStoreAndMobileCheck() && (
              <div
                className={`${styles['store-list']} ${!contactFieldEnabled && styles['store-list-without-contact']}`}
              >
                {state.storeOptionsData?.length > 0 &&
                  state.storeOptionsData.map(
                    (store: ICheckoutPickupStoresOptions) =>
                      store.id != selectedStoreId && (
                        <div
                          className={store.id === state.selectedStoreId ? styles['selectedStore'] : ''}
                          key={store.id}
                          id={store.id}
                        >
                          <CheckoutStoreOptionRadioCard
                            cardData={store}
                            isSelected={store.id === state.selectedStoreId}
                            dispatchAction={dispatchStateAction}
                            showAvailabilityTag={
                              showAvailabilityTag && state.storeOptionName == checkoutFulfillmentOptions.BOPIS
                            }
                          />
                        </div>
                      ),
                  )}
                {fulfillmentTypesAndStoreMap.storeMap[state.storeOptionName]?.lastEvaluatedKey && (
                  <div className={styles['view-all-stores']}>
                    <TextLink
                      id={styles['view-all-stores']}
                      data-testid="view-more-stores"
                      text={t('checkout.CheckoutPickupDrawer.viewMoreStores')}
                      onClick={viewMore}
                      textLinkStyle={TextLinkStyle.CHEVRONUNDERLINE}
                      iconPosition={TextLinkIconPosition.RIGHT}
                      rightIcon={{
                        iconName: 'chevron-down',
                        iconStyle: IconStyle.REGULAR,
                      }}
                      scroll={false}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const getOpenHoursInfo = () => (
    <div className={`${styles['store-details-fields']} ${styles['open-hours']}`}>
      <p className={styles['fields-label']}>{t('checkout.CheckoutPickupDrawer.openHoursLabel')}</p>
      {dayLabels.map((day) => (
        <p key={day} className={styles['field-value']}>
          <span className={styles['openHours-label']}>{t(`checkout.CheckoutPickupDrawer.${day}`)}</span>
          {state.storeDetailsData.schedule?.[day]?.isOpen
            ? `${state.storeDetailsData.schedule?.[day]?.openingTime} - ${state.storeDetailsData.schedule?.[day]?.closingTime}`
            : t('checkout.CheckoutPickupDrawer.closed')}
        </p>
      ))}
    </div>
  );

  const getStoreDetails = () => (
    <div className={styles['store-details-container']}>
      <h3 className={styles['store-details-name']}>
        <div className={styles['store-image']}>
          <NextgenImage
            src={fulfillmentCmsData?.[state.storeOptionName]?.iconUrl}
            alt={state.storeMap?.[state.storeOptionName]?.storeName}
            loaderType={LoaderType.CONTENTSTACK}
          />
        </div>
        {state.storeDetailsData.name}
      </h3>
      <div className={styles['store-details-fields']}>
        <p className={styles['fields-label']}>{t('checkout.CheckoutPickupDrawer.storeId')}</p>
        <p className={styles['fields-value']}>{state.storeDetailsData.id}</p>
      </div>
      <div className={styles['store-details-fields']}>
        <p className={styles['fields-label']}>{t('checkout.CheckoutPickupDrawer.storeAddress')}</p>
        <p className={styles['fields-value']}>{state.storeDetailsData.addressLine}</p>
        <p className={styles['fields-value']}>{state.storeDetailsData.phone}</p>
      </div>
      {getOpenHoursInfo()}
    </div>
  );

  const getDrawerFooter = () => {
    if (state.storeDetailsDrawerVisibility) {
      return (
        <div className={styles['checkout-pickup-form-btn']}>
          <Button
            title={t('checkout.CheckoutPickupDrawer.closeButtonText')}
            type={ButtonTypes.SUBMIT}
            data-testid="close-store-details"
            buttonStyle={ButtonStyles.SOLID}
            size={ButtonSize.MEDIUM}
            onClick={() =>
              dispatchStateAction({
                type: checkoutShippingDetailsActions.SET_STORE_DETAILS_DRAWER_VISIBILITY,
                payload: false,
              })
            }
          />
        </div>
      );
    } else if (state.pickupDrawerLoading) {
      return <SkeletonFrame className={styles['button-loader-bar']} />;
    } else if (showErrorCard) {
      return null;
    } else {
      return (
        <div className={styles['checkout-pickup-form-btn']}>
          <Button
            title={!confirmButtonLoading ? t('checkout.CheckoutPickupDrawer.confirmButtonText') : undefined}
            type={ButtonTypes.SUBMIT}
            buttonStyle={ButtonStyles.SOLID}
            size={ButtonSize.MEDIUM}
            disabled={
              !state.selectedStoreId ||
              (contactFieldEnabled &&
                (!state.recipientContact || !state.recipientCountryCode || !isValidPhoneNumber)) ||
              confirmButtonLoading
            }
            onClick={() =>
              onConfrimPickupStore(
                { countryCode: state.recipientCountryCode, mobileNo: state.recipientContact },
                state.storeOptionName,
                state.selectedServiceLocation,
              )
            }
            {...(confirmButtonLoading && {
              iconPosition: ButtonIconPosition.LEFT,
              icon: {
                iconName: 'spinner-third',
                iconAnimationClassName: IconAnimationClassName.SPIN,
              },
            })}
          />
        </div>
      );
    }
  };

  const handleStoreSearch = (value: string, keyName?: string) => {
    if (value && locationPermission == LocationPermission.GRANTED) {
      dispatchStateAction({ type: checkoutShippingDetailsActions.SET_ENABLE_LOCATION_CTA_VISIBILITY, payload: true });
    }
    if (
      keyName == 'Enter' &&
      value?.length &&
      value?.length == checkoutPostalCodeLength &&
      !isEasyDeliveryOrBopisOption(state.storeOptionName)
    ) {
      dispatchStateAction({ type: checkoutShippingDetailsActions.SET_STORE_MAP, payload: undefined });
      dispatch(resetPickupStoreOptions());
      getStoreList(value, state.storeOptionName, true);
    }
    if (isEasyDeliveryOrBopisOption(state.storeOptionName)) {
      dispatchStateAction({
        type: checkoutShippingDetailsActions.SET_STORE_OPTIONS_DATA,
        payload:
          value?.length >= 3 && initialPickupStoresList?.[state.storeOptionName]?.length
            ? filterArrayObjectByKeys(initialPickupStoresList?.[state.storeOptionName], storeFieldForSearch, value)
            : initialPickupStoresList?.[state.storeOptionName],
      });
    }
  };

  const onChangeStoreChipOption = (storeOptionName: string) => {
    setShowErrorCard(false);
    dispatchStateAction({
      type: checkoutShippingDetailsActions.SET_SELECTED_STORE_ID,
      payload: '',
    });
    dispatchStateAction({ type: checkoutShippingDetailsActions.SET_STORE_OPTION_NAME, payload: storeOptionName });
    const storepin = isEasyDeliveryOrBopisOption(storeOptionName)
      ? ''
      : fulfillmentTypesAndStoreMap?.pincode ||
        selectedStoreForPickup?.address?.postCode ||
        checkoutProfile?.fulfillmentType?.serviceLevel?.deliveryAddress?.postalCode ||
        '';
    storepin &&
      dispatchStateAction({
        type: checkoutShippingDetailsActions.SET_STORE_PIN,
        payload: locationPermission != LocationPermission.GRANTED ? storepin : '',
      });

    state.storeMap?.[storeOptionName]?.storeList.length
      ? setStoreData(storeOptionName)
      : locationPermission == LocationPermission.GRANTED
      ? showCurrentLocation(storeOptionName, false)
      : ((storepin || isEasyDeliveryOrBopisOption(storeOptionName)) &&
          fetchStoreListUsingDefaults(storepin, storeOptionName)) ||
        setStoreData(storeOptionName);
  };

  useEffect(() => {
    selectedStoreOptionRef.current = state.storeOptionName;
    if (
      !state.storePin &&
      !isEasyDeliveryOrBopisOption(state.storeOptionName) &&
      locationPermission != LocationPermission.GRANTED
    ) {
      dispatchStateAction({
        type: checkoutShippingDetailsActions.SET_STORE_PIN,
        payload:
          fulfillmentTypesAndStoreMap?.pincode ||
          selectedStoreForPickup?.address?.postCode ||
          checkoutProfile?.fulfillmentType?.serviceLevel?.deliveryAddress?.postalCode ||
          '',
      });
    } else if (isEasyDeliveryOrBopisOption(state.storeOptionName)) {
      dispatchStateAction({
        type: checkoutShippingDetailsActions.SET_STORE_PIN,
        payload: '',
      });
    }
  }, [state.storeOptionName]);

  useEffect(() => {
    if (!state.selectedStoreId && state.storeOptionsData?.length) {
      dispatchStateAction({
        type: checkoutShippingDetailsActions.SET_SELECTED_STORE_ID,
        payload: state.storeOptionsData[0]?.id,
      });
      dispatchStateAction({
        type: checkoutShippingDetailsActions.SET_SELECTED_SERVICE_LOCATION,
        payload: state.storeOptionsData[0]?.serviceLocation,
      });
    }
  }, [state.storeOptionsData]);

  const getLastUsedStore = () => {
    const selectedStoreId = selectedStoreForPickup?.id || checkoutProfile?.fulfillmentType?.serviceLocation?.id;
    const lastUsedStore: ICheckoutPickupStoresOptions =
      selectedStoreId &&
      state.storeOptionsData?.filter((store: ICheckoutPickupStoresOptions) => store.id == selectedStoreId)?.[0];
    return (
      <>
        {lastUsedStore && (
          <div className={styles['last-used-store']}>
            <h4 className={styles['last-used-store-header']}>{t('checkout.CheckoutPickupDrawer.lastStoreHeading')}</h4>
            <CheckoutStoreOptionRadioCard
              cardData={lastUsedStore}
              isSelected={lastUsedStore.id === state.selectedStoreId}
              dispatchAction={dispatchStateAction}
              showAvailabilityTag={showAvailabilityTag && state.storeOptionName == checkoutFulfillmentOptions.BOPIS}
            />
          </div>
        )}
      </>
    );
  };

  const scrollToSelectedStore = (storeId?: string) => {
    if (storeId) {
      document.getElementById(storeId)?.scrollIntoView();
    }
  };

  const onMapStoreSelection = (storeId: string) => {
    const selectedStore: ICheckoutPickupStoresOptions = state.storeOptionsData?.filter(
      (store: ICheckoutPickupStoresOptions) => store.id == storeId,
    )?.[0];
    if (selectedStore?.id) {
      dispatchStateAction({
        type: checkoutShippingDetailsActions.SET_SELECTED_STORE_ID,
        payload: selectedStore?.id,
      });
      dispatchStateAction({
        type: checkoutShippingDetailsActions.SET_SELECTED_SERVICE_LOCATION,
        payload: selectedStore?.serviceLocation,
      });
    }
    scrollToSelectedStore(selectedStore?.id);
  };

  const getDrawerWidth = () => {
    const drawerWidth = ((isDesktopView || isMdTabletView) && '80%') || (isTabletView && '90%') || undefined;
    return drawerWidth;
  };

  const getGoogleMapView = () => {
    const selectedStoreId = selectedStoreForPickup?.id || checkoutProfile?.fulfillmentType?.serviceLocation?.id;
    const lastUsedStore: ICheckoutPickupStoresOptions =
      selectedStoreId &&
      state.storeOptionsData?.filter((store: ICheckoutPickupStoresOptions) => store.id == selectedStoreId)?.[0];
    return (
      <>
        {state.storeOptionsData?.length > 0 && !loadingState?.getStoreLocations?.isLoading && (
          <div className={styles['pickup-stores-google-map']}>
            <GoogleMaps
              storeLocations={state.storeOptionsData.map((store: ICheckoutPickupStoresOptions) => ({
                id: store.id,
                name: store.name,
                lat: store.lat,
                lng: store.lng,
                storeName: store.name,
                storeId: store.id,
                storeAddress: store.addressLine,
              }))}
              selectedStoreId={state.selectedStoreId || state.storeOptionsData?.[0]?.id}
              customClass={`${styles['pickup-stores-google-map-view']} ${
                lastUsedStore && styles['pickup-stores-google-map-view-fullHeight']
              }`}
              getUpdatedStoreId={onMapStoreSelection}
              customMarkerURL={{
                defaultCustomMarkerURL: `${assetsBasePath}/images/${state.storeOptionName}-map-pin-default.svg`,
                selectedMarkerURL: `${assetsBasePath}/images/${state.storeOptionName}-map-pin-selected.svg`,
                scaledMarkerSize: {
                  defaultMarkerSize: {
                    height: 40,
                    width: 30,
                  },
                  selectedMarkerSize: {
                    height: 56,
                    width: 42,
                  },
                },
              }}
            />
          </div>
        )}
        {!state.storeOptionsData?.length &&
          !loadingState?.getStoreLocations?.isLoading &&
          (!showErrorCard || (showErrorCard && !isMobileView)) && (
            <ErrorCard
              className={styles['checkout-no-stores-card']}
              title={t('checkout.CheckoutPickupDrawer.noStoresFound')}
              imageUrl={`${assetsBasePath}${noStoreErrorImageUrl}`}
              type={Errors.General}
              customImageClass={styles['checkout-no-stores-card-image']}
              customCtaComponent={<></>}
            />
          )}
      </>
    );
  };

  const getLocationSuccessHandler = (position: GeolocationPosition, storeName: string, isPagination?: boolean) => {
    if (locationPermission != LocationPermission.GRANTED) {
      setLocationPermission(LocationPermission.GRANTED);
    }
    getStoreList('', storeName, false, position.coords.latitude, position.coords.longitude, isPagination);
  };

  const showCurrentLocation = (storeName: string, pagination: boolean) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => getLocationSuccessHandler(position, storeName, pagination),
        (position: unknown) => {
          logger.log('Checkout_PICKUP_DRAWER GEOLOCATION DENIED - ', position);
          setLocationPermission(LocationPermission.DENIED);
        },
      );
    }
  };

  const viewMore = () => {
    locationPermission == LocationPermission.GRANTED && !needPostalResetRef.current
      ? showCurrentLocation(state.storeOptionName, true)
      : ((state.storePin || isEasyDeliveryOrBopisOption(state.storeOptionName)) &&
          getStoreList(state.storePin, state.storeOptionName, true, undefined, undefined, true)) ||
        setStoreData(state.storeOptionName);
    if (isEasyDeliveryOrBopisOption(state.storeOptionName)) {
      dispatchStateAction({
        type: checkoutShippingDetailsActions.SET_STORE_PIN,
        payload: '',
      });
    }
  };

  return (
    <div className={styles['pickup-location-drawer-container']}>
      <Drawer
        header={
          <h4 className={styles['drawer-title']}>
            <span
              className={`${styles['backLink-icon-cta']} ${
                state.storeDetailsDrawerVisibility && styles['store-details-cta']
              }`}
            >
              <FAIcon
                theme={Theme.BLACK}
                iconName={'chevron-left'}
                shade={Shade.DARK}
                iconStyle={IconStyle.REGULAR}
                className={styles['backLink-icon']}
                onClick={() =>
                  !state.storeDetailsDrawerVisibility
                    ? onCloseDrawer()
                    : dispatchStateAction({
                        type: checkoutShippingDetailsActions.SET_STORE_DETAILS_DRAWER_VISIBILITY,
                        payload: false,
                      })
                }
              />
            </span>
            <span>
              {state.storeDetailsDrawerVisibility
                ? t('checkout.CheckoutStoreOptionRadioCard.storeDetails')
                : t('checkout.CheckoutPickupDrawer.drawerTitle')}
            </span>
          </h4>
        }
        closeOnBackdropClick={true}
        closeIcon={true}
        onClose={() => onCloseDrawer()}
        bottomGap={0}
        direction={DrawerDirections.FULLHEIGHT}
        footer={getDrawerFooter()}
        className={styles['checkout-location-drawer-container']}
        drawerWidth={getDrawerWidth()}
        useReactPortal={true}
        portalContainerId={'react-portal-drawer'}
      >
        {state.pickupDrawerLoading ? (
          <CheckoutPageLoader loaderType={LoaderTypeEnums.PICKUP_DRAWER} />
        ) : (
          <div
            className={`${styles['pickup-location-drawer-body']} ${
              !isMobileView && !state.storeDetailsDrawerVisibility && styles['pickup-location-drawer-body-2col']
            }`}
          >
            {!state.storeDetailsDrawerVisibility && (
              <>
                <div className={`${!isMobileView && styles['pickup-stores-fields-details']}`}>
                  {contactFieldEnabled && (
                    <PhoneNumber
                      name="Recipients Contact"
                      inputValue={state.recipientContact}
                      isAutoValidate={true}
                      hideDropdown={true}
                      onInputChange={(val: unknown, status?: string) => {
                        status == DropDownValidations.INVALID && setIsValidPhoneNumber(false);
                        status == DropDownValidations.VALID && setIsValidPhoneNumber(true);
                        dispatchStateAction({
                          type: checkoutShippingDetailsActions.SET_RECIPIENT_CONTACT,
                          payload: val as string,
                        });
                      }}
                      dropdownDisabled={true}
                      dropdownValue={state.recipientCountryCode || countryMasterData.defaultCountryCode}
                      configData={{
                        label: countryDropdownLabelField,
                        onSelect: (e) =>
                          dispatchStateAction({
                            type: checkoutShippingDetailsActions.SET_RECIPIENT_COUNTRY_CODE,
                            payload: e.callingCode as string,
                          }),
                      }}
                      inputLabel={t('checkout.CheckoutPickupDrawer.drawerFormLabelRecipientNumber')}
                      dropdownLabel={t('checkout.CheckoutPickupDrawer.country')}
                      data={countryMasterData.data}
                      disabledFilter
                      type="number"
                      isPhoneNumberValid={(isValid: boolean) => {
                        setIsValidPhoneNumber(isValid);
                        return isValid;
                      }}
                      autoLeadingZero={true}
                    />
                  )}
                  <h5
                    className={`${styles['pickup-location-label']} ${
                      !contactFieldEnabled && styles['pickup-location-label-top']
                    }`}
                  >
                    {t('checkout.CheckoutPickupDrawer.pickupFromLabel')}
                  </h5>
                  <div className={styles['pickup-postalCode-field']}>
                    <Input
                      id={styles['pickup-postalCode']}
                      data-testid="pickup-postalCode"
                      name="pickupPostalCode"
                      value={getValueCheck(needPostalResetRef.current, DataTypesEnums.TYPE_STRING) && state.storePin}
                      onChange={(val) => {
                        !needPostalResetRef.current && (needPostalResetRef.current = true);
                        dispatchStateAction({
                          type: checkoutShippingDetailsActions.SET_STORE_PIN,
                          payload: val.target.value || ('' as string),
                        });
                        handleStoreSearch(val.target.value);
                      }}
                      mlpVersion={MLPVersion.TWO}
                      {...getPostalFieldConfig()}
                    />
                    {state.storePin &&
                      !isEasyDeliveryOrBopisOption(state.storeOptionName) &&
                      state.storePin.length == checkoutPostalCodeLength && (
                        <div className={styles['search-icon-cta']}>
                          <FAIcon
                            theme={Theme.BLACK}
                            iconName={'search'}
                            shade={Shade.DARK}
                            iconStyle={IconStyle.REGULAR}
                            className={styles['search-icon']}
                            onClick={() => handleStoreSearch(state.storePin, 'Enter')}
                          />
                        </div>
                      )}
                  </div>
                  {state.enableLocationCtaVisibility && !isEasyDeliveryOrBopisOption(state.storeOptionName) && (
                    <div className={styles['enable-location-cta-container']}>
                      <TextLink
                        customClass={styles['enable-location-cta']}
                        id={'enable-location-cta'}
                        text={t('checkout.CheckoutPickupDrawer.enableLocationCta')}
                        onClick={() => {
                          showCurrentLocation(state.storeOptionName, false);
                        }}
                        leftIcon={{
                          iconName: 'location-crosshairs',
                        }}
                        textLinkStyle={TextLinkStyle.ICONUNDERLINE}
                        iconPosition={TextLinkIconPosition.LEFT}
                      />
                    </div>
                  )}
                  {state.storeTypeOptions.length > 0 && (
                    <div className={styles['store-options-container']}>
                      <div className={styles['store-chips']}>
                        {state.storeTypeOptions.map((store: string) => (
                          <div
                            onClick={() => onChangeStoreChipOption(store)}
                            data-testid="store-chip"
                            className={`${styles['store-chip']} ${
                              state.storeMap?.[store]?.isSelected ? styles['selected'] : ''
                            }`}
                            key={state.storeMap?.[store]?.storeName}
                            role="presentation"
                          >
                            {fulfillmentCmsData?.[store]?.iconUrl ? (
                              <div className={styles['store-icon']}>
                                <NextgenImage
                                  src={fulfillmentCmsData?.[store]?.iconUrl}
                                  alt={state.storeMap?.[store]?.storeName}
                                  loaderType={LoaderType.CONTENTSTACK}
                                />
                              </div>
                            ) : (
                              <FAIcon iconName={'image'} theme={Theme.GRAY} />
                            )}
                            <span>{state.storeMap?.[store]?.storeName}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {isMobileView && getGoogleMapView()}
                  {getErrorCard()}
                  {!showErrorCard && (
                    <>
                      {getLastUsedStore()}
                      {getListOfStores()}
                    </>
                  )}
                </div>
                {!isMobileView && <div className={styles['pickup-stores-map-details']}>{getGoogleMapView()}</div>}
              </>
            )}
            {state.storeDetailsDrawerVisibility && getStoreDetails()}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default CheckoutPickupDrawer;
